import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import ItemList from 'flarum/common/utils/ItemList';
import Link from 'flarum/common/components/Link';
import Discussion from 'flarum/common/models/Discussion';
import { dateTime, phpbbDate, readOnly, userLink } from './utils';
import SupportBlock from './components/SupportBlock';

/**
 * Post come in phpBB: titolo ("Re: …" dopo il primo), "by autore » data", testo e firma a sinistra; a destra il
 * profilo (avatar, nome, Posts, Joined, Location). Moduli caricati a parte da Flarum: si estendono per nome.
 */
export default function extendPosts() {
  // RUNE-72: quando Flarum porta a un post (link a un post, nuove risposte) lascia sopra lo spazio dell'intestazione
  // che resta fissa. Misura #header (o #app-navigation su telefono), che qui scorre con la pagina: quella fissa è
  // l'header del sito
  override('flarum/forum/components/PostStream', 'getMarginTop', function () {
    return this.$() && ($('.SiteHeader').outerHeight() || 0) + parseInt(this.$().css('margin-top'), 10);
  });
  override('flarum/forum/components/CommentPost', 'headerItems', function (original) {
    const items = original();
    const post = this.attrs.post;
    const discussion = post.discussion();
    const title = (post.number() > 1 ? 'Re: ' : '') + discussion.title();
    const permalink = app.route.discussion(discussion, post.number());
    items.has('user') && items.remove('user');
    items.has('meta') && items.remove('meta');
    items.has('edited') && items.remove('edited');
    items.add('phpbbTitle', m("h3", {
      className: "PostTitle"
    }, m(Link, {
      href: permalink
    }, title)), 110);
    items.add('phpbbAuthor', m("p", {
      className: "PostAuthor"
    }, m(Link, {
      className: "PostAuthor-target",
      href: permalink,
      title: "Post"
    }, m("span", {
      className: "sr-only"
    }, "Post")), "by ", m("strong", null, userLink(post.user())), " \xBB ", dateTime(post.createdAt())), 100);
    return items;
  });
  override('flarum/forum/components/CommentPost', 'sideItems', function () {
    const items = new ItemList();
    const user = this.attrs.post.user();
    if (!user) {
      items.add('profile', m("dl", {
        className: "PostProfile"
      }, m("dt", null, userLink(null))));
      return items;
    }
    const href = app.route('user', {
      username: user.id()
    });
    const location = user.attribute('phpbbLocation');
    const rank = user.attribute('phpbbRank');
    const website = user.attribute('phpbbWebsite');
    // avatar alle dimensioni con cui lo mostrava phpBB (max 90x90, non sempre quadrato), mai stirato
    const avatarW = user.attribute('phpbbAvatarWidth');
    const avatarH = user.attribute('phpbbAvatarHeight');
    items.add('profile', m("dl", {
      className: "PostProfile"
    }, m("dt", null, user.avatarUrl() && m(Link, {
      href: href,
      className: "PostProfile-avatar"
    }, m("img", {
      src: user.avatarUrl(),
      alt: "User avatar",
      width: avatarW || undefined,
      height: avatarH || undefined,
      loading: "lazy"
    })), userLink(user, null, 'PostProfile-name')), rank && m("dd", {
      className: "PostProfile-rank"
    }, rank), m("dd", null, "\xA0"), m("dd", null, m("strong", null, "Posts:"), " ", user.commentCount()), m("dd", null, m("strong", null, "Joined:"), " ", phpbbDate(user.joinTime())), location && m("dd", null, m("strong", null, "Location:"), " ", location), website && m("dd", null, m("ul", {
      className: "PostProfile-icons"
    }, m("li", {
      className: "PostProfile-web"
    }, m("a", {
      href: website,
      title: 'WWW: ' + website,
      target: "_blank",
      rel: "nofollow ugc noopener noreferrer"
    }, m("span", {
      className: "sr-only"
    }, "Website")))))));
    return items;
  });

  // blocchi di codice che scorrono: raggiungibili da tastiera (axe, scrollable-region-focusable)
  const focusableCode = function () {
    this.$('.Post-body pre').attr('tabindex', '0');
  };
  extend('flarum/forum/components/CommentPost', 'oncreate', focusableCode);
  extend('flarum/forum/components/CommentPost', 'onupdate', focusableCode);

  // PostStream ha role=feed ma contiene anche pause e segnaposto, non solo post (axe, aria-required-children)
  extend('flarum/forum/components/PostStream', 'view', function (vdom) {
    if (vdom?.attrs) delete vdom.attrs.role;
  });

  // in sola lettura niente "Write a Reply" per chi non può rispondere (Flarum lo mostra sempre agli ospiti). RUNE-78:
  // flarum/messages usa lo stesso componente con un dialogo al posto della discussione, senza canReply (e lì decide
  // già lui con canSendMessage): si filtrano solo le discussioni
  override('flarum/forum/components/ReplyPlaceholder', 'view', function (original) {
    const discussion = this.attrs.discussion;
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return readOnly() && discussion instanceof Discussion && !discussion.canReply() ? null : original(...args);
  });

  // in fondo ai post la barra "support RuneAudio", come dopo l'ultimo post di ogni pagina phpBB
  extend('flarum/forum/components/PostStream', 'endItems', function (items) {
    items.add('runeaudioSupport', m("div", {
      className: "PostStream-item",
      key: "runeaudio-support"
    }, m(SupportBlock, null)));
  });

  // freccia "Top" in fondo a ogni post, come la .back2top di phpBB: torna in cima alla pagina, cioè al primo post
  extend('flarum/forum/components/CommentPost', 'view', function (vdom) {
    if (!vdom?.children) return;
    vdom.children.push(m("div", {
      className: "Back2Top"
    }, m("a", {
      href: "#",
      title: "Top",
      onclick: e => {
        e.preventDefault();
        // nei topic lunghi Flarum tiene caricata solo una finestra di post: la cima della pagina è la cima della
        // finestra, e lì carica i post precedenti tenendo ferma la lettura, quindi si restava dov'eri. Si va al
        // primo post con lo stream (carica i primi post e scorre in cima), senza animazione come il resto (RUNE-67)
        const stream = app.current.get('stream');
        stream ? stream.goToIndex(0, true) : window.scrollTo({
          top: 0
        });
      }
    }, "Top")));
  });

  // "Last edited by … on …" in fondo al post, come la nota di phpBB
  extend('flarum/forum/components/CommentPost', 'footerItems', function (items) {
    const post = this.attrs.post;
    if (!post.isEdited?.() || post.isHidden()) return;
    items.add('phpbbEdited', m("div", {
      className: "PostNotice"
    }, "Last edited by ", userLink(post.editedUser()), " on ", dateTime(post.editedAt())), -100);
  });
}